import './util/handleError';
import './util/setupServiceWorker';
// Run session lockdown FIRST, before any module that may read MTProto
// localStorage / IndexedDB on import (cache.ts, sessions.ts, multitab.ts...).
import { applySessionLockdown } from './util/sessionLockdown';
applySessionLockdown();
import './global/init';
import './global/actions/all';

import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal,
} from './global';

import {
  DEBUG, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { selectChat, selectCurrentMessageList, selectPeerFullInfo, selectTabState } from './global/selectors';
import { selectSharedSettings } from './global/selectors/sharedState';
import { betterView } from './util/betterView';
import { IS_TAURI } from './util/browser/globalEnvironment';
import listenOtherClients from './util/browser/listenOtherClients';
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/browser/multitab';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { MULTITAB_STORAGE_KEY } from './util/multiaccount';
import { checkAndAssignPermanentWebVersion } from './util/permanentWebVersion';
import { onBeforeUnload } from './util/schedulers';
import initTauriApi from './util/tauri/initTauriApi';
import setupTauriListeners from './util/tauri/setupTauriListeners';
import updateWebmanifest from './util/updateWebmanifest';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';
import './styles/chat-only.scss';

// Standalone "chat-only" build: always activate chat-only mode.
// Note: do NOT pre-add `theme-dark` here — `switchTheme()` short-circuits when
// the class is already present and would skip applying CSS color variables,
// leaving header/bubbles light-themed. Theme is driven by initialState
// (`theme: 'dark'`) which triggers switchTheme normally.
try {
  document.body.classList.add('chat-only-mode');
  // Set dark background early to avoid white flash before switchTheme runs.
  document.documentElement.style.backgroundColor = '#212121';
} catch { /* ignore */ }

// Capture target chat id from URL hash IMMEDIATELY (before any module strips it).
// Format: `#<chatId>` (e.g. `#8229801423`). After auto-open we keep the hash in
// the URL so reload re-opens the same chat.
const TARGET_CHAT_ID: string | undefined = (() => {
  const raw = window.location.hash.replace(/^#/, '').trim();
  if (!raw) return undefined;
  // Only accept a plain numeric chat id; ignore complex hashes (auth tokens, etc.).
  return /^-?\d+$/.test(raw) ? raw : undefined;
})();

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

if (IS_TAURI) {
  initTauriApi();
  setupTauriListeners();
}

init();

async function init() {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  checkAndAssignPermanentWebVersion();
  // === Business-only lockdown ===
  // Skip cross-tab/cross-client BroadcastChannel hookups so a parallel tab
  // (whether of this app or any other origin-sharing webapp using the same
  // channel name) cannot inject an authenticated `global` into us via
  // `requestGlobal`, and so we do not advertise our own state outwards.
  // `listenOtherClients` is also skipped — the inactive-other-client banner
  // is irrelevant for an embedded chat.
  // listenOtherClients();
  // subscribeToMultitabBroadcastChannel();
  // await requestGlobal(APP_VERSION);
  void APP_VERSION;
  localStorage.setItem(MULTITAB_STORAGE_KEY, '1');
  onBeforeUnload(() => {
    const global = getGlobal();
    if (Object.keys(global.byTabId).length === 1) {
      localStorage.removeItem(MULTITAB_STORAGE_KEY);
    }
  });

  await initGlobal();
  // chat-embed runs in a single tab; mark this tab as the master so the
  // sync/loadAllChats pipeline (gated on `isMasterTab`) actually fires.
  getActions().init({ isMasterTab: true } as any);

  // Debug-log toggles are not used in the chat-embed build (their action
  // handlers live in `global/actions/ui/settings.ts` which is only loaded
  // when Main mounts, so calling them here would throw "is not a function").
  // getActions().updateShouldEnableDebugLog();
  // getActions().updateShouldDebugExportedSenders();

  const global = getGlobal();

  initLocalization(selectSharedSettings(global).language, true);

  subscribeToMasterChange((isMasterTab) => {
    // chat-embed: action handlers for `switchMultitabRole` are only loaded
    // once Main mounts (`actions/all`). Calling here too early throws
    // "is not a function". The multitab role does not affect chat-embed.
    void isMasterTab;
  });

  // chat-embed: kick off the API client (BusinessBotClient) directly. In the
  // upstream codebase this is triggered transitively by `switchMultitabRole`,
  // but we don't run that flow.
  getActions().initApi();
  const shouldReestablishMasterToSelf = getGlobal().auth.state !== 'authorizationStateReady';
  establishMultitabRole(shouldReestablishMasterToSelf);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    updateWebmanifest();

    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );

    betterView();
  });

  // Auto-open the target chat from the URL hash. Polls until the chat appears
  // in `global.chats.byId`, then dispatches `openChat` and verifies that the
  // tab's `messageLists` actually picked it up. Adds `body.chat-loaded` only
  // once the chat is truly active so the gate CSS reveals the MiddleColumn.
  //
  // IMPORTANT: This poll dispatches `openChat` AT MOST ONCE. Re-dispatching on
  // every tick (as we used to) creates a race with the parent shell's
  // `qsr-open-chat` postMessage bridge — the user clicks chat B, our handler
  // switches to B, then the poll snaps the message list back to A on its next
  // tick. That race manifested as partial / chunked / out-of-order messages.
  //
  // The poll is also aborted the moment the parent posts a `qsr-open-chat`
  // (see _qsrAborted flag below). After that, the parent fully owns chat
  // selection and we must not interfere.
  let _qsrAborted = false;
  if (TARGET_CHAT_ID) {
    const start = Date.now();
    let dispatched = false;
    const poll = () => {
      if (_qsrAborted) {
        // Parent took over; reveal whatever is on screen and stop polling.
        document.body.classList.add('chat-loaded');
        return;
      }
      const g = getGlobal();
      const tabId = Object.keys(g.byTabId)[0];
      const tab = tabId ? g.byTabId[tabId] : undefined;
      const isActive = !!tab?.messageLists?.some((ml) => ml.chatId === TARGET_CHAT_ID);
      // Also wait for the MiddleHeader to actually render the chat (title text
      // present), so we don't unhide an in-between empty state.
      const headerRendered = !!document.querySelector('.MiddleHeader .info .title')?.textContent?.trim();

      if (isActive && headerRendered) {
        if (window.location.hash !== `#${TARGET_CHAT_ID}`) {
          history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}#${TARGET_CHAT_ID}`);
        }
        document.body.classList.add('chat-loaded');
        return;
      }

      const ready = g.auth?.state === 'authorizationStateReady';
      if (ready && !dispatched) {
        // Dispatch ONCE — the same action a native chat-list click uses.
        // Telegram-Web-A's MessageList container will react to the
        // messageLists update and load viewport messages on its own.
        getActions().openChat(
          { id: TARGET_CHAT_ID, shouldReplaceHistory: true } as any,
          { forceOnHeavyAnimation: true } as any,
        );
        dispatched = true;
      }

      if (Date.now() - start > 15000) {
        // Safety: never leave the user on a black screen indefinitely.
        document.body.classList.add('chat-loaded');
        return;
      }
      setTimeout(poll, 100);
    };
    // Defer first poll until after the initial render task settles.
    setTimeout(poll, 0);
  } else {
    // No deep-link: nothing to wait for, reveal immediately.
    document.body.classList.add('chat-loaded');
  }

  // ── External chat-switch bridge (qsr) ──────────────────────────────────────
  // Allow the parent window (the Quasar shell embedding this iframe) to switch
  // the active chat without reloading the iframe. The parent posts:
  //   { type: 'qsr-open-chat', chatId: '<numeric id>' }
  // and we dispatch `openChat` (the same action a native chat-list click uses).
  // JS bundle, network connection and already-fetched chats stay alive —
  // chat switch is instant.
  window.addEventListener('message', (e: MessageEvent) => {
    try {
      const data = e?.data;
      if (!data || typeof data !== 'object' || data.type !== 'qsr-open-chat') return;
      const chatId = String(data.chatId || '').trim();
      if (!/^-?\d+$/.test(chatId)) return;

      // Stop the deep-link auto-open poll — once the parent drives chat
      // selection via postMessage, the poll's re-dispatch would race with us
      // and snap the message list back to TARGET_CHAT_ID, causing partial /
      // out-of-order rendering.
      _qsrAborted = true;

      const switchNow = () => {
        try {
          // `openChat` is what the native chat-list item click dispatches
          // (see components/left/main/Chat.tsx). It updates the active chat
          // for the current tab AND pushes history correctly.
          getActions().openChat(
            { id: chatId, shouldReplaceHistory: true } as any,
            { forceOnHeavyAnimation: true } as any,
          );
          if (window.location.hash !== `#${chatId}`) {
            history.replaceState(history.state, '', `${window.location.pathname}${window.location.search}#${chatId}`);
          }
          document.body.classList.add('chat-loaded');
        } catch { /* ignore — will retry */ }
      };

      // Dispatch immediately if the chat is already in our cache, otherwise
      // poll briefly until it's loaded (fresh chats can take ~100-300 ms).
      const start = Date.now();
      const tryOpen = () => {
        const g = getGlobal();
        const authReady = g.auth?.state === 'authorizationStateReady';
        if (authReady) {
          // Even if the chat object is not yet in `byId`, dispatching openChat
          // is safe — Telegram-Web-A loads it on demand. Only gate on auth.
          switchNow();
          return;
        }
        if (Date.now() - start > 8000) return;
        setTimeout(tryOpen, 80);
      };
      tryOpen();

      // Acknowledge so the parent can stop showing a transient spinner.
      try {
        e.source && (e.source as Window).postMessage(
          { type: 'qsr-open-chat-ack', chatId },
          { targetOrigin: e.origin || '*' },
        );
      } catch { /* ignore */ }
    } catch { /* ignore malformed */ }
  });

  // Tell the parent we are ready to receive `qsr-open-chat` messages.
  // Repeat a few times — the parent may attach its listener slightly later
  // (StrictMode dev re-mounts, or HMR), and a single post can be missed.
  const announceReady = () => {
    try { window.parent?.postMessage({ type: 'qsr-ready' }, '*'); } catch { /* ignore */ }
  };
  announceReady();
  setTimeout(announceReady, 100);
  setTimeout(announceReady, 500);
  setTimeout(announceReady, 1500);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      const currentGlobal = getGlobal();
      const currentMessageList = selectCurrentMessageList(currentGlobal);
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(currentGlobal));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', currentGlobal);
      if (currentMessageList) {
        // eslint-disable-next-line no-console
        console.warn(
          'CURRENT MESSAGE LIST',
          selectChat(currentGlobal, currentMessageList.chatId),
          selectPeerFullInfo(currentGlobal, currentMessageList.chatId),
          currentGlobal.messages.byChatId[currentMessageList.chatId],
        );
      }
    });
  }
}

onBeforeUnload(() => {
  const actions = getActions();
  actions.leaveGroupCall?.({ isPageUnload: true });
  actions.hangUp?.({ isPageUnload: true });
});
